import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import './scenario-planner-edit.modal.scss';
import '../../../../components/filters/components/filter-tab-button/filter-tab-button.component.scss';
import { Button, Spinner } from 'react-bootstrap';
import { DescriptorsComponent } from '../../../../components/descriptor/descriptor.component';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import { REPORT_NAMES } from '../../../../components/reports/constants/report-names.constants';
import { AppTabularColumn } from '../../../../components/reports/dtos/app-tabular.dto';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { FilterSelectedValue } from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import {
  formatNumber,
  formatPercNumber,
} from '../../../../utils/formatting/number-formatter.helper';
import { MassEditBlock } from '../mass-edit-sidebar/mass-edit-sidebar.component';
import ScenarioPlannerEditInputFieldComponent from './scenario-planner-edit-input-field.component';

interface StartingMetricRow {
  id: string;
  name?: string;
  category_name?: string;
  [key: string]: any;
}

interface ScenarioPlannerEditStartingMetricsComponentProps {
  isSaving?: boolean;
  onClose: (show: boolean) => void;
  scenarioPlannerId: string;
  disabled?: boolean;
  applyToFilterSelection: FilterSelectedValue[];
  onComplete: (
    inputFields: MassEditBlock[],
    invalidateQuery: () => Promise<void>,
  ) => void;
  resetButton: ReactNode;
}

export const ScenarioPlannerEditStartingMetricsComponent = ({
  isSaving,
  onClose,
  scenarioPlannerId,
  onComplete,
  disabled,
  applyToFilterSelection,
  resetButton,
}: ScenarioPlannerEditStartingMetricsComponentProps) => {
  // Dynamic form state based on API response measure columns. This is updated byt the user inputs.
  const [startingMetrics, setStartingMetrics] = useState<{
    [key: string]: string;
  }>();
  // This stores initial starting metrics and once it is loaded doesn't change.
  const [initMetrics, setInitMetrics] = useState<{
    [key: string]: string;
  }>();

  const sortBy = useMemo(() => [{ id: 'name', desc: false }], []);

  const { data, isFetching, invalidateQuery } =
    useDataProvider<StartingMetricRow>(
      REPORT_NAMES.SCENARIO_PLANNER_STARTING_METRIC,
      applyToFilterSelection,
      sortBy,
      true,
      true,
      scenarioPlannerId,
    );
  const handleChange = useCallback((field: string, value: any) => {
    setStartingMetrics((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const columns = useMemo(() => {
    return data?.columns ?? [];
  }, [data]);

  const measureColumns = useMemo(() => {
    return data?.measureColumns ?? [];
  }, [data]);
  // We expect only one row since a product has one or none data.
  const currentRowData: StartingMetricRow | undefined = useMemo(() => {
    const row = data?.rows[0];
    return row;
  }, [data?.rows]);

  // Initialize starting metrics form data when API data loads
  useEffect(() => {
    if (isFetching) {
      // Clear values to be prepared for upcoming result.
      setStartingMetrics(undefined);
      setInitMetrics(undefined);
      return;
    }
    const measureColumns = data?.measureColumns;
    if (!measureColumns || measureColumns.length === 0) return;
    const values: Record<string, string> = {};
    measureColumns.forEach((i) => {
      if (currentRowData) {
        const isOverwritten: boolean =
          currentRowData[`${i.id}IsOverwritten`] ?? false;
        if (isOverwritten) {
          values[i.id] = currentRowData[i.id];
        }
      }
    });
    // Values to be modified by the user.
    setStartingMetrics(values);
    // Initial values to display saved one.
    setInitMetrics(values);
  }, [currentRowData, data, isFetching]);
  const handleComplete = useCallback(() => {
    const inputFields: MassEditBlock[] = [];
    if (!currentRowData || !startingMetrics) return;
    // Dynamically create input fields based on elasticity values
    Object.entries(startingMetrics).forEach(([fieldId, value]) => {
      const col = measureColumns.find((i) => i.id === fieldId);
      const isOverwritten: boolean =
        currentRowData[`${fieldId}IsOverwritten`] ?? false;
      let numberValue = parseFloat(value);
      if (col?.format === 'percentage') {
        // For example, Depth Of Discount is a percentage, therefore, we allow the user to enter number from -100 to 100.
        // But we store a number between -1 and 1
        numberValue = numberValue / 100;
      }

      if (value && value !== '') {
        // New values
        inputFields.push({
          fieldId: `start_${fieldId}`, // Prefix with 'start_' for starting metrics
          inputType: 'abs',
          value: numberValue,
        });
      }
      if (isOverwritten && value === '') {
        // Erased values
        inputFields.push({
          fieldId: `start_${fieldId}`, // Prefix with 'start_' for starting metrics
          inputType: 'abs',
          value: undefined,
        });
      }
    });
    onComplete(inputFields, invalidateQuery);
  }, [
    currentRowData,
    startingMetrics,
    onComplete,
    invalidateQuery,
    measureColumns,
  ]);

  const internalDisabled = useMemo(() => {
    return isFetching || isSaving || disabled;
  }, [isFetching, isSaving, disabled]);
  const getPlaceholder = (
    currentRowData: StartingMetricRow | undefined,
    col: AppTabularColumn,
  ) => {
    const raw = currentRowData ? currentRowData[`${col.id}Original`] : '';
    if (col.format === 'percentage') {
      return `${formatPercNumber((raw ?? 0) * 100)}%`;
    } else {
      return `${formatNumber(raw, col.format, col.format?.currencyCode)}`;
    }
  };
  const getInitMetric = (
    iMetrics: {
      [key: string]: string;
    },
    col: AppTabularColumn,
  ) => {
    const raw = iMetrics[col.id];
    if (raw !== undefined && col.format === 'percentage') {
      const n = Number.parseFloat(raw ?? '0') * 100;
      const formattedN = formatNumber(n, {
        maximumFractionDigits: 5,
        minimumFractionDigits: 0,
      });
      return formattedN;
    } else {
      return raw;
    }
  };

  return (
    <div className="p-4">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <LoadingComponent isLoading={isFetching}>
              <div className="table-container">
                <table className="table table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.id}
                          className={`${col.descriptors ? 'descriptor' : ''}`}
                        >
                          {col.name}
                        </th>
                      ))}
                      {measureColumns.map((col) => (
                        <th key={col.id}>{col.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={`${col.descriptors ? 'descriptor' : ''}`}
                        >
                          {col.descriptors && (
                            <div className="prod-cell-text">
                              <div>
                                <div>
                                  {currentRowData?.metadata
                                    ? currentRowData?.metadata.description
                                    : ''}
                                </div>
                                <DescriptorsComponent
                                  metadata={currentRowData?.metadata}
                                />
                              </div>
                            </div>
                          )}
                          {col.descriptors === false &&
                            currentRowData &&
                            currentRowData[col.id]}
                        </td>
                      ))}
                      {measureColumns.map((col) => {
                        return (
                          <td key={col.id}>
                            {initMetrics && (
                              <ScenarioPlannerEditInputFieldComponent
                                value={getInitMetric(initMetrics, col)}
                                isOverwritten={
                                  currentRowData &&
                                  currentRowData[`${col.id}IsOverwritten`]
                                }
                                placeholder={getPlaceholder(
                                  currentRowData,
                                  col,
                                )}
                                onChange={handleChange}
                                fieldId={col.id}
                                disabled={internalDisabled}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </LoadingComponent>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-end gap-3">
            {resetButton}
            <Button
              variant="outline-danger"
              onClick={() => (onClose ? onClose(false) : null)}
              disabled={internalDisabled}
            >
              Discard Changes
            </Button>
            <Button
              variant="primary"
              onClick={handleComplete}
              disabled={internalDisabled}
            >
              {isSaving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                'Save Base Metrics'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioPlannerEditStartingMetricsComponent;
